/**
 * BookingPortal — /book/:slug
 *
 * Public booking page. No auth required.
 * Brand themed from integration.product_name:
 *   velvet → luxury (Cormorant Garamond, warm parchment, gold)
 *   recon  → confidence (Inter, crisp blue)
 *   null   → Cal neutral (clean slate)
 *
 * Emotionally correct: "Feels like your personal assistant owns your schedule."
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../api';
import { getTheme } from '../theme';
import {
  fmtDate,
  fmtTime,
  fmtDateTime,
  getWeekStart,
  getWeekDays,
  isSameDay,
  isToday,
  isPast,
  generateICS,
  downloadBlob,
  tzAbbr,
  DAY_LABELS,
} from '../utils';

// ─── Timezone Selector ───────────────────────────────────────────────────────
// Common IANA timezones grouped for the picker
// US timezones only — labeled the way Americans think about them
const TZ_OPTIONS = [
  { tz: 'America/New_York',  label: 'Eastern Time'          },
  { tz: 'America/Chicago',   label: 'Central Time'           },
  { tz: 'America/Denver',    label: 'Mountain Time'          },
  { tz: 'America/Phoenix',   label: 'Mountain Time (Arizona)'},
  { tz: 'America/Los_Angeles', label: 'Pacific Time'         },
  { tz: 'America/Anchorage', label: 'Alaska Time'            },
  { tz: 'Pacific/Honolulu',  label: 'Hawaii Time'            },
];

function tzLabel(tz) {
  // Use friendly label if available
  const found = TZ_OPTIONS.find(o => o.tz === tz);
  if (found) {
    try {
      const abbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
        .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
      return `${found.label} (${abbr})`;
    } catch { return found.label; }
  }
  // Fallback for unknown zones (owner might be outside US)
  try {
    const abbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date()).find(p => p.type === 'timeZoneName')?.value || '';
    const city = tz.split('/').pop().replace(/_/g, ' ');
    return `${city} (${abbr})`;
  } catch { return tz; }
}

function TzSelector({ T, ownerTz, viewerTz, setViewerTz }) {
  const [open, setOpen] = React.useState(false);
  const localTz = React.useMemo(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ownerTz; }
  }, [ownerTz]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, flexWrap: 'wrap', position: 'relative' }}>
      {/* Globe icon */}
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <circle cx="12" cy="12" r="10"/>
        <line x1="2" y1="12" x2="22" y2="12"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
      <span style={{ fontFamily: T.fontUI, fontSize: '0.8rem', color: T.textMuted }}>Times in</span>

      {/* Tappable timezone badge — obviously interactive */}
      <button
        className="cal-action-btn"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 5,
          fontFamily: T.fontUI, fontSize: '0.82rem', fontWeight: 500,
          color: T.accent,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3,
        }}
      >
        {viewerTz === localTz ? `Your local time (${tzLabel(viewerTz)})` : tzLabel(viewerTz)}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          {open ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
        </svg>
      </button>

      {/* Quick toggle if local ≠ owner tz */}
      {localTz !== ownerTz && viewerTz !== localTz && (
        <button
          style={{ fontFamily: T.fontUI, fontSize: '0.78rem', color: T.textMuted,
            background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          onClick={() => setViewerTz(localTz)}
        >
          · Use my local time
        </button>
      )}

      {/* Full picker dropdown */}
      {open && (
        <div style={{ position: 'relative', width: '100%' }}>
          <div style={{
            position: 'absolute', top: 4, left: 0, zIndex: 200,
            background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
            border: `1px solid ${T.border || '#E5E5E5'}`, overflow: 'hidden',
            maxHeight: 'none', overflowY: 'visible',
          }}>
            {/* US timezone list — no scroll needed */}
            {TZ_OPTIONS.map(({ tz: t, label }) => (
              <button key={t}
                onClick={() => { setViewerTz(t); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', textAlign: 'left',
                  padding: '11px 16px',
                  background: t === viewerTz ? `${T.accent}12` : 'none',
                  border: 'none', borderBottom: '1px solid #F5F3F0',
                  fontFamily: T.fontUI, fontSize: '0.875rem',
                  color: t === viewerTz ? T.accent : T.text, cursor: 'pointer',
                  fontWeight: t === viewerTz ? 600 : 400,
                }}
              >
                <span>{tzLabel(t)}</span>
                {t === localTz && (
                  <span style={{ fontSize: '0.72rem', color: T.textMuted }}>Your time</span>
                )}
                {t === viewerTz && t !== localTz && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Calendar Add Menu ───────────────────────────────────────────────────────
function CalendarAddMenu({ T, calendarData, booking, selectedSlot, generateICS, downloadBlob }) {
  const [open, setOpen] = React.useState(false);

  const startAt = selectedSlot?.start_at || booking?.starts_at || booking?.start_at;
  const endAt = selectedSlot?.end_at || booking?.ends_at || booking?.end_at;
  const title = encodeURIComponent(`${calendarData?.name || 'Booking'} with ${calendarData?.owner_name || ''}`);
  const details = encodeURIComponent(`Booking reference: ${booking?.booking_id || ''}`);

  // Format date for Google Calendar: YYYYMMDDTHHmmssZ
  function toGCal(iso) {
    if (!iso) return '';
    return new Date(iso).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  }

  const gcalUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${toGCal(startAt)}/${toGCal(endAt)}&details=${details}`;
  const outlookUrl = `https://outlook.live.com/calendar/0/action/compose?subject=${title}&startdt=${startAt}&enddt=${endAt}&body=${details}`;

  function handleICS(label) {
    const ics = generateICS({
      summary: `${calendarData?.name || 'Booking'} with ${calendarData?.owner_name || ''}`,
      description: `Booking reference: ${booking?.booking_id || ''}`,
      start: startAt,
      end: endAt,
    });
    downloadBlob(ics, 'booking.ics', 'text/calendar');
    setOpen(false);
  }

  const btnStyle = {
    display: 'flex', alignItems: 'center', width: '100%', textAlign: 'left', padding: '10px 16px',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.9rem',
    color: T.text, fontFamily: T.fontUI,
    transition: 'background 0.12s',
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="cal-btn-secondary"
        style={{
          padding: '10px 20px', borderRadius: 8, border: `1.5px solid ${T.accent}`,
          background: 'transparent', color: T.accent, fontFamily: T.fontUI,
          fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', letterSpacing: '0.01em',
          display: 'flex', alignItems: 'center', gap: 8,
        }}
        onClick={() => setOpen(o => !o)}
      >
        <span>Add to calendar</span>
        <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', bottom: '110%', left: 0, minWidth: 220,
          background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.14)',
          border: `1px solid ${T.border || '#E5E5E5'}`, overflow: 'hidden', zIndex: 100,
        }}>
          {/* Google Calendar */}
          <a
            href={gcalUrl} target="_blank" rel="noopener noreferrer"
            style={{ ...btnStyle, textDecoration: 'none', display: 'flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => setOpen(false)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:8,flexShrink:0}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Google Calendar
          </a>
          {/* Apple Calendar / iCal */}
          <button
            style={btnStyle}
            onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => handleICS('apple')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:8,flexShrink:0}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Apple Calendar
          </button>
          {/* Outlook */}
          <a
            href={outlookUrl} target="_blank" rel="noopener noreferrer"
            style={{ ...btnStyle, textDecoration: 'none', borderTop: '1px solid #F0F0F0' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => setOpen(false)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:8,flexShrink:0}}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>Outlook
          </a>
          {/* Generic .ics */}
          <button
            style={{ ...btnStyle, borderTop: '1px solid #F0F0F0', color: '#888', fontSize: '0.8rem', display: 'flex', alignItems: 'center' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F5F5F5'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
            onClick={() => handleICS('ics')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:8,flexShrink:0,opacity:0.5}}><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Download .ics file
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Slot grouping by day ─────────────────────────────────────────────────────
function groupSlotsByDay(slots = []) {
  const map = {};
  for (const slot of slots) {
    const day = new Date(slot.start_at).toDateString();
    if (!map[day]) map[day] = [];
    map[day].push(slot);
  }
  return map;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function BookingPortal() {
  const { slug } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calendarData, setCalendarData] = useState(null);
  const [theme, setTheme] = useState(getTheme(null));

  // Week navigation
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Selected day for mobile date strip
  const [selectedDay, setSelectedDay] = useState(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', location_type: '', location_note: '' });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Confirmation
  const [booking, setBooking] = useState(null);
  const [bookedLocationType, setBookedLocationType] = useState(null);
  const [bookedLocationNote, setBookedLocationNote] = useState('');

  // ─── Load calendar ─────────────────────────────────────────────────────────
  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await publicApi.getCalendar(slug);
        setCalendarData(data);
        setTheme(getTheme(data?.integration?.product_name));
        if (data.location_types && data.location_types.length > 0) {
          setForm(f => ({
            ...f,
            location_type: f.location_type || data.location_types[0],
            // Pre-fill location_note with owner address if in_person is default and address exists
            location_note: f.location_note || (data.location_types[0] === 'in_person' && data.location_address) ? (data.location_address || '') : '',
          }));
        }
      } catch (err) {
        if (err.status === 404) {
          setError('not_found');
        } else {
          setError('network');
        }
      }
      setLoading(false);
    }
    load();
  }, [slug]);

  // ─── Week navigation ───────────────────────────────────────────────────────
  const prevWeek = useCallback(() => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
    setSelectedSlot(null);
    setSelectedDay(null);
  }, []);

  const nextWeek = useCallback(() => {
    setWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
    setSelectedSlot(null);
    setSelectedDay(null);
  }, []);

  // ─── Form validation ───────────────────────────────────────────────────────
  function validate() {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    return errs;
  }

  // ─── Submit booking ────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }
    setSubmitting(true);
    setFormErrors({});
    try {
      const payload = {
        slug,
        slot_id: selectedSlot?.id,
        starts_at: selectedSlot?.start_at,
        ends_at: selectedSlot?.end_at,
        booker_location_type: form.location_type || calendarData?.location_types?.[0] || 'video',
        booker_location_note: form.location_note || null,
        participant: {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
        },
      };
      const result = await publicApi.createBooking(payload);
      setBookedLocationType(form.location_type || calendarData?.location_types?.[0] || 'video');
      setBookedLocationNote(form.location_note || '');
      setBooking(result);
    } catch (err) {
      if (err.status === 409) {
        setFormErrors({ _global: 'This slot was just taken — please pick another.' });
        setShowForm(false);
        setSelectedSlot(null);
      } else {
        setFormErrors({ _global: 'Something went wrong. Please try again.' });
      }
    }
    setSubmitting(false);
  }

  // ─── Grouped slots for visible week ────────────────────────────────────────
  const weekDays = getWeekDays(weekStart);
  const slotsByDay = groupSlotsByDay(calendarData?.available_slots?.slots || calendarData?.available_slots || []);
  // Timezone: default to booker's local TZ, fall back to owner TZ
  const ownerTz = calendarData?.timezone || 'America/New_York';
  const [viewerTz, setViewerTz] = React.useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return ownerTz; }
  });
  const tz = viewerTz;

  // Auto-select: prefer today if it has slots, else first future day with slots, else first non-past day
  useEffect(() => {
    if (!selectedDay || !weekDays.some(d => isSameDay(d, selectedDay))) {
      const today = new Date();
      const todayDay = weekDays.find(d => isSameDay(d, today));
      const todayHasSlots = todayDay && slotsByDay[todayDay.toDateString()]?.length > 0;
      const firstFutureWithSlots = weekDays.find(d => {
        const k = d.toDateString();
        return !isPast(d) && slotsByDay[k] && slotsByDay[k].length > 0;
      });
      const firstNonPast = weekDays.find(d => !isPast(d) || isSameDay(d, today));
      setSelectedDay(todayHasSlots ? todayDay : firstFutureWithSlots || firstNonPast || weekDays[0]);
    }
  }, [weekStart, JSON.stringify(Object.keys(slotsByDay))]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render helpers ────────────────────────────────────────────────────────
  const T = theme;
  const isVelvet = T.brand === 'velvet';
  const isRecon = T.brand === 'recon';

  // Inject fonts
  useEffect(() => {
    if (isVelvet) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&family=Inter:wght@300;400;500;600&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    } else if (isRecon) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }, [isVelvet, isRecon]);

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={S.page(T)}>
        <div style={S.loadingWrap}>
          <div style={S.spinner(T)} />
          <p style={{ color: T.textMuted, fontFamily: T.fontBody, marginTop: 16, fontSize: 14 }}>
            Loading calendar…
          </p>
        </div>
      </div>
    );
  }

  // ─── Error: not found ──────────────────────────────────────────────────────
  if (error === 'not_found') {
    return (
      <div style={{ ...S.page(getTheme(null)), justifyContent: 'center', alignItems: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 32, marginBottom: 20, opacity: 0.7 }}>◻</div>
          <h1 style={{ fontFamily: getTheme(null).fontDisplay, fontSize: '1.5rem', color: getTheme(null).text, marginBottom: 12, fontWeight: 600 }}>
            This link isn't active yet.
          </h1>
          <p style={{ color: getTheme(null).textMuted, fontFamily: getTheme(null).fontBody, maxWidth: 340, lineHeight: 1.6 }}>
            The person who shared this link may still be setting things up. Try reaching out to them directly.
          </p>
        </div>
      </div>
    );
  }

  // ─── Error: network ────────────────────────────────────────────────────────
  if (error === 'network') {
    return (
      <div style={{ ...S.page(getTheme(null)), justifyContent: 'center', alignItems: 'center', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ textAlign: 'center', padding: '40px 24px' }}>
          <div style={{ fontSize: 32, marginBottom: 20, opacity: 0.7 }}>◻</div>
          <h1 style={{ fontFamily: getTheme(null).fontDisplay, fontSize: '1.5rem', color: getTheme(null).text, marginBottom: 12 }}>
            Connection issue
          </h1>
          <p style={{ color: getTheme(null).textMuted, fontFamily: getTheme(null).fontBody, marginBottom: 24 }}>
            Couldn't load this calendar. Please try again.
          </p>
          <button
            className="cal-btn-primary"
            onClick={() => window.location.reload()}
            style={S.btn(getTheme(null))}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ─── Confirmation view ─────────────────────────────────────────────────────
  if (booking) {
    return (
      <div style={S.page(T)}>
        <div style={S.container}>
          {/* Branded header */}
          <Header calendarData={calendarData} theme={T} />

          {/* Confirmation card */}
          <div style={S.confirmCard(T)}>
            <div className="cal-confirm-check" style={S.confirmCheck(T)}>✓</div>
            <div className="cal-confirm-body">
              <h2 style={S.confirmTitle(T)}>
                {isVelvet ? "You're booked." : "Booking confirmed."}
              </h2>
              <p style={S.confirmSub(T)}>
                {isVelvet
                  ? `${calendarData.owner_name} has your spot confirmed.`
                  : `Your appointment with ${calendarData.owner_name} is set.`
                }
              </p>

              <div style={S.confirmDetail(T)}>
                <div style={S.confirmRow(T)}>
                  <span style={S.confirmLabel(T)}>Date &amp; time</span>
                  <span style={S.confirmValue(T)}>
                    {selectedSlot
                      ? fmtDateTime(selectedSlot.start_at, tz)
                      : booking.starts_at
                        ? fmtDateTime(booking.starts_at, tz)
                        : 'Confirmed'
                    }
                    {tz && <span style={{ color: T.textMuted, fontSize: '0.85em', marginLeft: 6 }}>({tzAbbr(tz)})</span>}
                  </span>
                </div>
                <div style={S.confirmRow(T)}>
                  <span style={S.confirmLabel(T)}>With</span>
                  <span style={S.confirmValue(T)}>{calendarData.owner_name}</span>
                </div>
                {bookedLocationType && (() => {
                  const locationConfig = {
                    video:     { label: 'Video call',  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z"/></svg> },
                    phone:     { label: 'Phone call',  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 11 19.79 19.79 0 0 1 1.05 2.5 2 2 0 0 1 3 .35h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 7.91a16 16 0 0 0 6.1 6.1l.41-.41a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/></svg> },
                    in_person: { label: 'In person',   icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg> },
                  };
                  const cfg = locationConfig[bookedLocationType] || { label: bookedLocationType, icon: null };
                  return (
                    <div style={S.confirmRow(T)}>
                      <span style={S.confirmLabel(T)}>Meeting type</span>
                      <span style={{ ...S.confirmValue(T), display: 'flex', alignItems: 'center', gap: 5 }}>
                        {cfg.icon}{cfg.label}
                      </span>
                    </div>
                  );
                })()}
                {booking.booking_id && (
                  <div style={S.confirmRow(T)}>
                    <span style={S.confirmLabel(T)}>Reference</span>
                    <span style={{ ...S.confirmValue(T), fontFamily: T.fontMono, fontSize: '0.8rem', letterSpacing: '0.05em' }}>
                      {booking.booking_id}
                    </span>
                  </div>
                )}
                {bookedLocationType === 'in_person' && bookedLocationNote && (
                  <div style={S.confirmRow(T)}>
                    <span style={S.confirmLabel(T)}>Location</span>
                    <span style={S.confirmValue(T)}>{bookedLocationNote}</span>
                  </div>
                )}
              </div>

              <CalendarAddMenu
                T={T}
                calendarData={calendarData}
                booking={booking}
                selectedSlot={selectedSlot}
                generateICS={generateICS}
                downloadBlob={downloadBlob}
              />


              {bookedLocationType === 'in_person' && (() => {
                const mapsAddress = bookedLocationNote || calendarData?.location_address;
                if (!mapsAddress) return null;
                return (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <a
                      href={"https://maps.apple.com/?q=" + encodeURIComponent(mapsAddress)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '10px 18px',
                        borderRadius: 20,
                        background: '#fff',
                        border: "1px solid " + T.border,
                        color: T.accent,
                        fontSize: 13,
                        fontFamily: T.fontUI,
                        fontWeight: 500,
                        textDecoration: 'none',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                        <circle cx="12" cy="10" r="3"/>
                      </svg>
                      Open in Apple Maps
                    </a>
                    <p style={{ fontSize: 12, color: T.textSecondary, marginTop: 6, fontFamily: T.fontUI }}>
                      {mapsAddress}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main booking flow ─────────────────────────────────────────────────────
  const canGoPrev = weekStart > getWeekStart(new Date());

  return (
    <div style={S.page(T)}>
      <div style={S.container}>
        {/* Branded header */}
        <Header calendarData={calendarData} theme={T} />

        {/* Slot picker */}
        {!showForm && (
          <>
          {/* Meeting meta strip */}
          <div style={{
            display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24,
            padding: '12px 18px',
            background: '#FFFFFF',
            borderRadius: 10,
            border: `1px solid ${T.border}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.fontUI, fontSize: 13, color: T.textSecondary }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              30 min
            </span>
            {/* Location type — show in meta strip only if exactly one option */}
            {(calendarData?.location_types?.length === 1 || (!calendarData?.location_types && calendarData?.location_type)) && (() => {
              const lt = calendarData?.location_types?.[0] || calendarData?.location_type || 'video';
              const locationConfig = {
                video: {
                  label: 'Video call',
                  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z"/></svg>,
                },
                phone: {
                  label: 'Phone call',
                  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 11 19.79 19.79 0 0 1 1.05 2.5 2 2 0 0 1 3 .35h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 7.91a16 16 0 0 0 6.1 6.1l.41-.41a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/></svg>,
                },
                in_person: {
                  label: 'In person',
                  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
                },
              };
              const cfg = locationConfig[lt] || locationConfig.video;
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: T.fontUI, fontSize: 13, color: T.textSecondary }}>
                  {cfg.icon}
                  {cfg.label}
                </span>
              );
            })()}
          </div>
          <div style={S.pickerSection}>
            {/* Timezone selector */}
            <TzSelector T={T} ownerTz={ownerTz} viewerTz={viewerTz} setViewerTz={setViewerTz} />

            {/* Week nav */}
            <div style={S.weekNav(T)}>
              <button
                className="cal-nav-btn"
                style={S.weekNavBtn(T, !canGoPrev)}
                disabled={!canGoPrev}
                onClick={prevWeek}
                aria-label="Previous week"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <span style={S.weekLabel(T)}>
                {fmtDate(weekStart, null, { month: 'short', day: 'numeric' })}
                {' – '}
                {fmtDate(weekDays[6], null, { month: 'short', day: 'numeric' })}
              </span>
              <button
                className="cal-nav-btn"
                style={S.weekNavBtn(T, false)}
                onClick={nextWeek}
                aria-label="Next week"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Date strip — horizontal scrollable */}
            <div style={{...S.dayStrip, scrollSnapType: 'x mandatory'}}>
              {weekDays.map((day, i) => {
                const dayKey = day.toDateString();
                const today = isToday(day);
                const dayPast = isPast(new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59));
                const isSelected = selectedDay && isSameDay(day, selectedDay);
                const hasSlots = (slotsByDay[dayKey] || []).length > 0;

                return (
                  <div
                    key={dayKey}
                    className="cal-day-item"
                    style={{
                      ...S.dayStripItem(T, isSelected, today, hasSlots),
                      opacity: dayPast ? 0.3 : !hasSlots ? 0.45 : 1,
                    }}
                    onClick={() => (!dayPast && hasSlots) && setSelectedDay(day)}
                    role="button"
                    aria-label={`${DAY_LABELS[i]} ${day.getDate()}`}
                    tabIndex={dayPast ? -1 : 0}
                    onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && !dayPast && hasSlots && setSelectedDay(day)}
                  >
                    <span style={S.dayStripLabel(T, isSelected)}>{DAY_LABELS[i]}</span>
                    <span style={S.dayStripNum(T, isSelected, today)}>{day.getDate()}</span>
                    {hasSlots && (
                      <span style={S.dayStripDot(T, isSelected)} />
                    )}
                  </div>
                );
              })}
            </div>

            {/* Slot list for selected day */}
            {selectedDay && (() => {
              const dayKey = selectedDay.toDateString();
              const daySlots = (slotsByDay[dayKey] || []).sort(
                (a, b) => new Date(a.start_at) - new Date(b.start_at)
              );
              return (
                <div style={{ position: 'relative' }}>
                <div style={S.slotVerticalList} className="cal-slots-list" key={selectedDay?.toDateString()}>
                  {/* Slot count badge */}
                  {daySlots.length > 0 && (
                    <div style={{ fontFamily: T.fontUI, fontSize: '0.82rem', color: T.textSecondary,
                      marginBottom: 12, textAlign: 'left', letterSpacing: '0.01em', fontWeight: 500 }}>
                      {daySlots.length} {daySlots.length === 1 ? 'time available' : 'times available'} on {selectedDay.toLocaleDateString('en-US', { weekday: 'long' })}
                    </div>
                  )}
                  {daySlots.length === 0 ? (
                    <div style={S.noSlotsMsg(T)}>
                      No available times this day
                      {(() => {
                        const nextDay = weekDays.find(d => {
                          const k = d.toDateString();
                          return d > selectedDay && slotsByDay[k]?.length > 0;
                        });
                        return nextDay ? (
                          <button
                            onClick={() => setSelectedDay(nextDay)}
                            style={{ display: 'block', margin: '10px auto 0', background: 'none', border: 'none',
                              fontFamily: T.fontUI, fontSize: '0.82rem', color: T.accent, cursor: 'pointer',
                              textDecoration: 'underline', textUnderlineOffset: 3 }}
                          >
                            Next available: {new Date(nextDay).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </button>
                        ) : null;
                      })()}
                    </div>
                  ) : (
                    daySlots.map(slot => {
                      const slotPast = isPast(slot.start_at);
                      const taken = slot.status === 'booked' || slot.status === 'unavailable';
                      const selected = selectedSlot?.start_at === slot.start_at;
                      const disabled = slotPast || taken;
                      const duration = slot.end_at
                        ? Math.round((new Date(slot.end_at) - new Date(slot.start_at)) / 60000)
                        : null;

                      return (
                        <button
                          key={slot.start_at}
                          className="cal-slot"
                          style={S.slotPillFull(T, selected, disabled)}
                          disabled={disabled}
                          onClick={() => {
                            if (selectedSlot?.start_at === slot.start_at) {
                              setSelectedSlot(null);  // deselect
                            } else {
                              setSelectedSlot(slot);
                            }
                            setFormErrors({});
                          }}
                          aria-label={`${fmtTime(slot.start_at, tz)}${taken ? ' (unavailable)' : ''}`}
                        >
                          <span>
                            {fmtTime(slot.start_at, tz)}
                          </span>
                          {taken && (
                            <span style={{ fontSize: 12, color: T.textMuted }}>Unavailable</span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
                {daySlots.length > 4 && (
                  <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 48,
                    background: `linear-gradient(to bottom, transparent, ${T.bg})`,
                    pointerEvents: 'none',
                  }} />
                )}
                </div>
              );
            })()}

            {/* Global form error (slot taken) */}
            {formErrors._global && (
              <div style={S.globalError(T)}>{formErrors._global}</div>
            )}

            {/* Selected slot → show form CTA */}
            {selectedSlot && (
              <div style={S.selectedBar(T)}>
                <span style={{ color: T.text, fontFamily: T.fontBody, fontSize: 14 }}>
                  <strong>{fmtDateTime(selectedSlot.start_at, tz)}</strong>
                  {tz && <span style={{ color: T.textMuted, marginLeft: 6 }}>({tzAbbr(tz)})</span>}
                </span>
                <button
                  className="cal-btn-primary"
                  style={S.btn(T)}
                  onClick={() => setShowForm(true)}
                >
                  {isVelvet ? 'Reserve this time' : 'Continue →'}
                </button>
              </div>
            )}


          </div>
          </>
        )}

        {/* Booking form */}
        {showForm && selectedSlot && (
          <BookingForm
            theme={T}
            selectedSlot={selectedSlot}
            tz={tz}
            form={form}
            setForm={setForm}
            formErrors={formErrors}
            submitting={submitting}
            isVelvet={isVelvet}
            calendarData={calendarData}
            onSubmit={handleSubmit}
            onBack={() => { setShowForm(false); setFormErrors({}); }}
          />
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function Header({ calendarData, theme: T }) {
  const isVelvet = T.brand === 'velvet';
  const initials = (calendarData?.owner_name || '')
    .split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('');
  const avatarAccent = T.brand === 'velvet' ? '#B8860B' : T.brand === 'recon' ? '#1D4ED8' : (T.accent || '#D97706');
  return (
    <header style={S.header(T)}>
      {isVelvet && (
        <div style={S.velvetBrand}>
          <span style={S.velvetBrandText}>Velvet</span>
        </div>
      )}
      {T.brand === 'recon' && (
        <div style={S.reconBrand}>
          <span style={S.reconBrandText}>Recon</span>
        </div>
      )}
      {T.brand === 'cal' && (
        <div style={{ fontFamily: T.fontUI, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.15em', color: T.textMuted, textTransform: 'uppercase', marginBottom: 12 }}>
          Cal
        </div>
      )}
      {/* Photo takes priority; fall back to initials circle */}
      {calendarData?.owner_photo_url ? (
        <img
          src={calendarData.owner_photo_url}
          alt={calendarData.owner_name}
          className="cal-avatar"
          style={{ objectFit: 'cover', border: `2px solid ${avatarAccent}30` }}
        />
      ) : initials ? (
        <div className="cal-avatar" style={{ background: `${avatarAccent}1A`, color: avatarAccent }}>
          {initials}
        </div>
      ) : null}
      <h1 style={S.ownerName(T)}>{calendarData?.owner_name}</h1>
      {calendarData?.name && calendarData.name !== calendarData.owner_name && (
        <h2 style={S.calName(T)}>{calendarData?.name}</h2>
      )}
      {calendarData?.description && (
        <p style={{
          fontFamily: T.fontBody,
          fontSize: '0.875rem',
          color: T.textMuted,
          margin: '8px auto 0',
          maxWidth: 380,
          lineHeight: 1.6,
        }}>
          {calendarData.description}
        </p>
      )}

    </header>
  );
}

function BookingForm({ theme: T, selectedSlot, tz, form, setForm, formErrors, submitting, isVelvet, calendarData, onSubmit, onBack }) {
  return (
    <div style={S.formSection(T)}>
      <div style={S.selectedSlotDisplay(T)}>
        <span style={{ fontSize: 16, marginRight: 8, opacity: 0.6 }}>◷</span>
        <div>
          <div style={{ fontFamily: T.fontBody, fontWeight: 500, color: T.text, fontSize: 14 }}>
            {fmtDate(selectedSlot.start_at, tz, { weekday: 'long', month: 'long', day: 'numeric' })}
          </div>
          <div style={{ fontFamily: T.fontBody, color: T.textSecondary, fontSize: 13 }}>
            {fmtTime(selectedSlot.start_at, tz)} – {fmtTime(selectedSlot.end_at, tz)}
            {tz && <span style={{ color: T.textMuted, marginLeft: 6 }}>({tzAbbr(tz)})</span>}
          </div>
        </div>
        <button
          className="cal-btn-secondary"
          style={S.backLink(T)}
          onClick={onBack}
          type="button"
        >
          Change
        </button>
      </div>

      <h3 style={S.formTitle(T)}>
        {isVelvet ? 'Your details' : 'Confirm your information'}
      </h3>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={S.fieldGroup}>
          <label style={S.label(T)}>Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            style={S.input(T, !!formErrors.name)}
            placeholder={isVelvet ? 'Your full name' : 'Full name'}
            autoComplete="name"
          />
          {formErrors.name && <span style={S.fieldError(T)}>{formErrors.name}</span>}
        </div>

        <div style={S.fieldGroup}>
          <label style={S.label(T)}>Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            style={S.input(T, !!formErrors.email)}
            placeholder="your@email.com"
            autoComplete="email"
          />
          {formErrors.email && <span style={S.fieldError(T)}>{formErrors.email}</span>}
        </div>

        <div style={S.fieldGroup}>
          <label style={S.label(T)}>Phone <span style={{ color: T.textMuted }}>(optional)</span></label>
          <input
            type="tel"
            value={form.phone}
            onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
            style={S.input(T, false)}
            placeholder="+1 (555) 000-0000"
            autoComplete="tel"
          />
        </div>

        {/* Meeting type picker — only shown when owner allows multiple formats */}
        {(calendarData?.location_types?.length > 1) && (
          <div style={{ marginBottom: 16 }}>
            <label style={S.label(T)}>
              How would you like to meet?
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {calendarData.location_types.map(lt => {
                const config = {
                  video:     { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 10l4.553-2.069A1 1 0 0 1 21 8.845v6.31a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8z"/></svg>, label: 'Video call' },
                  phone:     { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.07 11 19.79 19.79 0 0 1 1.05 2.5 2 2 0 0 1 3 .35h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 7.91a16 16 0 0 0 6.1 6.1l.41-.41a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/></svg>, label: 'Phone call' },
                  in_person: { icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>, label: 'In person' },
                };
                const cfg = config[lt] || { icon: null, label: lt };
                const selected = form.location_type === lt;
                return (
                  <button
                    key={lt}
                    type="button"
                    onClick={() => {
                      setForm(f => ({
                        ...f,
                        location_type: lt,
                        // Pre-fill with owner address if in_person selected and field empty
                        location_note: lt === 'in_person' && !f.location_note && calendarData?.location_address
                          ? calendarData.location_address
                          : f.location_note,
                      }));
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: 20,
                      border: `1px solid ${selected ? T.accent : T.border}`,
                      background: selected ? T.accent + '18' : '#fff',
                      color: selected ? T.accent : T.textSecondary,
                      fontFamily: T.fontUI,
                      fontSize: 13,
                      fontWeight: selected ? 600 : 400,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    {cfg.icon}{' '}{cfg.label}
                  </button>
                );
              })}
            </div>
            {/* Location proposal — shown when in_person is selected */}
            {form.location_type === 'in_person' && (
              <div style={{ marginTop: 10 }}>
                <input
                  type="text"
                  placeholder={calendarData?.location_address
                    ? `Suggest a location (or use: ${calendarData.location_address})`
                    : 'Suggest a meeting location'}
                  value={form.location_note || ''}
                  onChange={e => setForm(f => ({ ...f, location_note: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '9px 12px',
                    border: `1px solid ${T.border}`,
                    borderRadius: 8,
                    fontSize: 13,
                    fontFamily: T.fontUI,
                    color: T.text,
                    background: '#fff',
                    boxSizing: 'border-box',
                  }}
                />
                <span style={{ fontSize: 11, color: T.textSecondary, fontFamily: T.fontUI, marginTop: 4, display: 'block' }}>
                  Optional — owner will confirm the location
                </span>
              </div>
            )}
          </div>
        )}

        {formErrors._global && (
          <div style={S.globalError(T)}>{formErrors._global}</div>
        )}

        <button
          type="submit"
          className="cal-btn-primary"
          style={{ ...S.btn(T), marginTop: 8, opacity: submitting ? 0.7 : 1 }}
          disabled={submitting}
        >
          {submitting
            ? 'Booking…'
            : isVelvet
              ? 'Confirm reservation'
              : 'Confirm booking'
          }
        </button>
      </form>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const S = {
  page: (T) => ({
    minHeight: '100vh',
    background: T.bg,
    fontFamily: T.fontBody,
  }),
  container: {
    maxWidth: 760,
    margin: '0 auto',
    padding: '0 16px 80px',
    paddingBottom: 100,
  },
  loadingWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
  },
  spinner: (T) => ({
    width: 32,
    height: 32,
    border: `2px solid ${T.border}`,
    borderTopColor: T.accent,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  }),

  // Header
  header: (T) => ({
    textAlign: 'center',
    padding: T.brand === 'velvet' ? '56px 24px 40px' : '40px 24px 32px',
    borderBottom: `1px solid ${T.border}`,
    marginBottom: T.brand === 'velvet' ? 48 : 32,
  }),
  velvetBrand: {
    marginBottom: 16,
  },
  velvetBrandText: {
    fontFamily: "'Cormorant Garamond', Georgia, serif",
    fontSize: '0.8rem',
    fontWeight: 600,
    letterSpacing: '0.3em',
    textTransform: 'uppercase',
    color: '#B8860B',
  },
  reconBrand: {
    marginBottom: 12,
  },
  reconBrandText: {
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: '#1D4ED8',
  },
  ownerName: (T) => ({
    fontFamily: T.fontDisplay,
    fontSize: T.displaySize,
    fontWeight: T.brand === 'velvet' ? 400 : 700,
    color: T.text,
    margin: '0 0 8px',
    lineHeight: 1.2,
    letterSpacing: T.brand === 'velvet' ? '-0.025em' : 'normal',
  }),
  calName: (T) => ({
    fontFamily: T.fontBody,
    fontSize: '1rem',
    fontWeight: 400,
    color: T.textSecondary,
    margin: '0 0 8px',
  }),
  tz: (T) => ({
    fontFamily: T.fontBody,
    fontSize: '0.8rem',
    color: T.textMuted,
    margin: 0,
  }),

  // Slot picker
  pickerSection: {
    // mobile-first
  },
  weekNav: (T) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
    padding: '0 4px',
  }),
  weekNavBtn: (T, disabled) => ({
    background: 'none',
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    width: 36,
    height: 36,
    color: disabled ? T.textMuted : T.text,
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: disabled ? 0.4 : 1,
    transition: 'all 0.15s',
    padding: 0,
    lineHeight: 0,
  }),
  weekLabel: (T) => ({
    fontFamily: T.fontBody,
    fontSize: 14,
    color: T.textSecondary,
    fontWeight: 500,
  }),
  // Date strip (replaces dayGrid)
  dayStrip: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 4,
    paddingBottom: 4,
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },
  dayStripItem: (T, isSelected, isToday, hasSlots) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 3,
    flex: 1,
    minWidth: 40,
    maxWidth: 56,
    padding: '10px 4px',
    borderRadius: 12,
    cursor: 'pointer',
    border: `1.5px solid ${isSelected ? '#5C3D2A' : 'transparent'}`,
    background: isSelected ? '#5C3D2A' : 'transparent',
    flexShrink: 0,
    userSelect: 'none',
    transition: `background 0.15s, border-color 0.15s`,
  }),
  dayStripLabel: (T, isSelected) => ({
    fontFamily: T.fontBody,
    fontSize: 10,
    fontWeight: 600,
    color: isSelected ? '#FFFFFF' : T.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    lineHeight: 1,
  }),
  dayStripNum: (T, isSelected, isToday) => ({
    fontFamily: T.fontBody,
    fontSize: 20,
    fontWeight: isSelected ? 700 : 500,
    color: isSelected ? '#FFFFFF' : T.text,
    lineHeight: 1,
  }),
  dayStripDot: (T, isSelected) => ({
    width: 4,
    height: 4,
    borderRadius: '50%',
    background: isSelected ? 'rgba(255,255,255,0.6)' : T.accent,
    display: 'block',
    marginTop: 3,
  }),
  // Vertical slot list (replaces slotList)
  slotVerticalList: {
    animation: 'calSlotsIn 0.22s ease both',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 20,
  },
  noSlotsMsg: (T) => ({
    color: T.textMuted,
    fontFamily: T.fontBody,
    fontSize: 14,
    padding: '20px 0',
    textAlign: 'center',
  }),
  slotPillFull: (T, selected, disabled) => ({
    width: '100%',
    padding: '14px 20px',
    border: `1.5px solid ${selected ? T.accent : disabled ? T.border : T.border}`,
    borderRadius: 10,
    background: selected ? T.accent : disabled ? T.surfaceAlt : T.surface,
    color: selected ? '#FFFFFF' : disabled ? T.textMuted : T.text,
    fontFamily: T.fontBody,
    fontSize: 15,
    fontWeight: selected ? 600 : 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: `all ${T.transition}`,
    textAlign: 'left',
    opacity: disabled ? 0.4 : 1,
    outline: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxSizing: 'border-box',
    boxShadow: selected ? `0 2px 8px ${T.accent}40` : disabled ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
  }),
  selectedBar: (T) => ({
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    padding: '16px 20px',
    background: T.surface,
    borderTop: `1px solid ${T.border}`,
    boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
    animation: 'calBarIn 0.22s ease both',
  }),
  globalError: (T) => ({
    background: T.brand === 'velvet' ? '#FEF2F2' : '#FEF2F2',
    border: `1px solid ${T.error}30`,
    borderRadius: 8,
    padding: '12px 16px',
    color: T.error,
    fontFamily: T.fontBody,
    fontSize: 13,
    marginTop: 16,
  }),

  // Form
  formSection: (T) => ({
    padding: T.brand === 'velvet' ? '0 0 40px' : '0 0 32px',
  }),
  selectedSlotDisplay: (T) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '16px 20px',
    background: T.surfaceAlt,
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    marginBottom: 32,
  }),
  backLink: (T) => ({
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: T.accent,
    fontFamily: T.fontBody,
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
    padding: 0,
    textDecoration: 'underline',
  }),
  formTitle: (T) => ({
    fontFamily: T.brand === 'velvet' ? T.fontDisplay : T.fontBody,
    fontSize: T.brand === 'velvet' ? '1.3rem' : '1.1rem',
    fontWeight: T.brand === 'velvet' ? 400 : 600,
    color: T.text,
    margin: '0 0 24px',
  }),
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: (T) => ({
    fontFamily: T.fontBody,
    fontSize: 13,
    color: T.textSecondary,
    fontWeight: 500,
  }),
  input: (T, hasError) => ({
    padding: '12px 14px',
    border: `1px solid ${hasError ? T.error : T.border}`,
    borderRadius: 8,
    fontFamily: T.fontBody,
    fontSize: 15,
    color: T.text,
    background: T.surface,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    transition: `border-color ${T.transition}`,
  }),
  fieldError: (T) => ({
    color: T.error,
    fontFamily: T.fontBody,
    fontSize: 12,
  }),

  // Buttons
  btn: (T) => ({
    background: T.accent,
    color: T.accentText,
    border: 'none',
    borderRadius: 8,
    padding: '13px 24px',
    fontFamily: T.fontBody,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: `background ${T.transition}`,
    letterSpacing: T.brand === 'velvet' ? '0.05em' : 'normal',
  }),

  // Confirmation
  confirmCard: (T) => ({
    textAlign: 'center',
    padding: T.brand === 'velvet' ? '64px 40px' : '48px 32px',
    maxWidth: 480,
    margin: '0 auto',
  }),
  confirmCheck: (T) => ({
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: T.brand === 'velvet' ? '#B8860B20' : T.brand === 'recon' ? '#1D4ED820' : '#4361EE20',
    color: T.accent,
    fontSize: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 24px',
    fontWeight: 700,
  }),
  confirmTitle: (T) => ({
    fontFamily: T.fontDisplay,
    fontSize: T.brand === 'velvet' ? '2rem' : '1.5rem',
    fontWeight: T.brand === 'velvet' ? 400 : 700,
    color: T.text,
    margin: '0 0 12px',
    letterSpacing: T.brand === 'velvet' ? '-0.025em' : 'normal',
  }),
  confirmSub: (T) => ({
    fontFamily: T.fontBody,
    color: T.textSecondary,
    fontSize: 15,
    margin: '0 0 32px',
  }),
  confirmDetail: (T) => ({
    background: T.surfaceAlt,
    border: `1px solid ${T.border}`,
    borderRadius: 10,
    padding: '20px 24px',
    marginBottom: 24,
    textAlign: 'left',
  }),
  confirmRow: (T) => ({
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    padding: '8px 0',
    borderBottom: `1px solid ${T.border}`,
    ':last-child': { borderBottom: 'none' },
  }),
  confirmLabel: (T) => ({
    fontFamily: T.fontBody,
    fontSize: 13,
    color: T.textMuted,
  }),
  confirmValue: (T) => ({
    fontFamily: T.fontBody,
    fontSize: 13,
    color: T.text,
    fontWeight: 500,
    textAlign: 'right',
  }),
  icsBtn: (T) => ({
    background: 'none',
    border: `1px solid ${T.accent}`,
    color: T.accent,
    borderRadius: 8,
    padding: '10px 20px',
    fontFamily: T.fontBody,
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 500,
  }),
};
